// Welcome Day Editor — authors the welcome_day.json the viewer reads. Same
// two-pane feel as the viewer, but the itinerary and resources are editable:
// add/reorder-by-time events, edit each event's info, place pins on the map
// (click to place or drag the marker), and edit the backpack resources.
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  loadWelcomeDay, saveWelcomeDay, sortedEvents, prettyTime, makeEvent, makeResource,
} from './welcomeDay.js';
import ImagePicker from './ImagePicker.jsx';
import RichBodyEditor from './RichBodyEditor.jsx';
import WelcomeBackpack from './WelcomeBackpack.jsx';
import { loadAllMapFiles } from '../pathfinder/lib/dataSource.js';
import { buildMapData } from '../pathfinder/lib/graph.js';
import './welcomeday.css';

const CENTER = { lat: 49.80939016935401, lng: -97.1343494222327 };
const nodeLabel = (n) => (n?.room_number || n?.label || n?.type || 'node');

// Lightweight room search over the pathfinder room index (building or room #).
function searchRoomList(rooms, query, limit = 18) {
  const tokens = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  const scored = [];
  for (const r of rooms) {
    const room = String(r.roomNumber).toLowerCase();
    const bldg = String(r.buildingName).toLowerCase();
    let score = 0, ok = true;
    for (const t of tokens) {
      if (room === t) score += 100;
      else if (room.startsWith(t)) score += 50;
      else if (room.includes(t)) score += 20;
      else if (bldg.includes(t)) score += 10;
      else { ok = false; break; }
    }
    if (ok) scored.push({ r, score });
  }
  scored.sort((a, b) => b.score - a.score || String(a.r.roomNumber).localeCompare(String(b.r.roomNumber), undefined, { numeric: true }));
  return scored.slice(0, limit).map(s => s.r);
}

export default function WelcomeDayEditor({ dataSource = null, apiKey: apiKeyProp = null }) {
  const apiKey = apiKeyProp || process.env.REACT_APP_MAPS_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey });

  const [data, setData] = useState(null);
  const [view, setView] = useState('events');    // 'events' | 'resources'
  const [selEvent, setSelEvent] = useState(null); // event id
  const [selRes, setSelRes] = useState(null);     // resource id
  const [arrSel, setArrSel] = useState(null);     // resource selected while arranging the backpack
  const [placing, setPlacing] = useState(false);  // arm map-click to set selected event location
  const [picking, setPicking] = useState(false);  // arm node-click to add a checkpoint
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [graphReady, setGraphReady] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const markerObjsRef = useRef(new Map());
  const mapDataRef = useRef(null);
  const placingRef = useRef(placing); useEffect(() => { placingRef.current = placing; }, [placing]);
  const pickingRef = useRef(picking); useEffect(() => { pickingRef.current = picking; }, [picking]);
  const selEventRef = useRef(selEvent); useEffect(() => { selEventRef.current = selEvent; }, [selEvent]);
  const selResRef = useRef(selRes); useEffect(() => { selResRef.current = selRes; }, [selRes]);

  useEffect(() => { loadWelcomeDay().then(setData); }, []);
  const events = useMemo(() => sortedEvents(data?.events || []), [data]);

  // Pathfinding graph → room search + checkpoint node picking.
  useEffect(() => {
    if (!dataSource) return;
    let cancelled = false;
    loadAllMapFiles(dataSource).then(f => { if (!cancelled) { mapDataRef.current = buildMapData(f); setGraphReady(true); } }).catch(() => {});
    return () => { cancelled = true; };
  }, [dataSource]);
  const roomIndex = useMemo(() => (graphReady ? (mapDataRef.current.roomIndex || []).filter(r => r.roomNumber) : []), [graphReady]);

  // ── Mutators ────────────────────────────────────────────────────────────
  const mutate = useCallback((fn) => { setData(d => { const nd = fn(d); return nd; }); setDirty(true); }, []);
  const updateEvent = useCallback((id, patch) => mutate(d => ({ ...d, events: d.events.map(e => e.id === id ? { ...e, ...(typeof patch === 'function' ? patch(e) : patch) } : e) })), [mutate]);
  const updateRes = useCallback((id, patch) => mutate(d => ({ ...d, resources: d.resources.map(r => r.id === id ? { ...r, ...patch } : r) })), [mutate]);

  // Resource pickup location: a nav node (from room search) students can route to.
  const setResLocation = useCallback((entry) => {
    const node = mapDataRef.current?.nodes.get(entry.nodeId);
    updateRes(selResRef.current, {
      destNodeId: entry.nodeId, location: `${entry.buildingName} ${entry.roomNumber}`,
      ...(node ? { lat: node.lat, lng: node.lng } : {}),
    });
  }, [updateRes]);
  const clearResLocation = useCallback(() => updateRes(selResRef.current, { destNodeId: null, location: '' }), [updateRes]);

  // ── Wayfinding: destination room + ordered checkpoints ────────────────────
  const setDestination = useCallback((entry) => {
    const node = mapDataRef.current?.nodes.get(entry.nodeId);
    updateEvent(selEventRef.current, {
      destNodeId: entry.nodeId, location: `${entry.buildingName} ${entry.roomNumber}`,
      ...(node ? { lat: node.lat, lng: node.lng } : {}),
    });
    setStatus(`Destination set: ${entry.buildingName} ${entry.roomNumber}`);
  }, [updateEvent]);
  const clearDestination = useCallback(() => updateEvent(selEventRef.current, { destNodeId: null }), [updateEvent]);
  const addCheckpoint = useCallback((node) => {
    updateEvent(selEventRef.current, e => ({ checkpoints: [...(e.checkpoints || []), { nodeId: node.id, label: nodeLabel(node) }] }));
    setStatus(`Checkpoint added: ${nodeLabel(node)}`);
  }, [updateEvent]);
  const addCheckpointRef = useRef(addCheckpoint); useEffect(() => { addCheckpointRef.current = addCheckpoint; }, [addCheckpoint]);
  const removeCheckpoint = useCallback((i) => updateEvent(selEventRef.current, e => ({ checkpoints: (e.checkpoints || []).filter((_, j) => j !== i) })), [updateEvent]);
  const moveCheckpoint = useCallback((i, dir) => updateEvent(selEventRef.current, e => {
    const cps = [...(e.checkpoints || [])]; const j = i + dir;
    if (j < 0 || j >= cps.length) return {};
    [cps[i], cps[j]] = [cps[j], cps[i]]; return { checkpoints: cps };
  }), [updateEvent]);

  const addEvent = useCallback(() => {
    const c = mapInstanceRef.current?.getCenter?.();
    const ev = makeEvent(c ? { lat: c.lat(), lng: c.lng() } : { ...CENTER });
    mutate(d => ({ ...d, events: [...d.events, ev] }));
    setSelEvent(ev.id); setView('events'); setPlacing(true);
    setStatus('Click the map to place this event, or drag its pin.');
  }, [mutate]);
  const deleteEvent = useCallback((id) => {
    const e = data?.events.find(x => x.id === id);
    if (e && !window.confirm(`Delete "${e.title || 'this event'}"?`)) return;
    mutate(d => ({ ...d, events: d.events.filter(x => x.id !== id) }));
    setSelEvent(s => (s === id ? null : s));
  }, [data, mutate]);

  const addRes = useCallback(() => {
    const r = makeResource();
    mutate(d => ({ ...d, resources: [...d.resources, r] }));
    setSelRes(r.id); setView('resources');
  }, [mutate]);
  const deleteRes = useCallback((id) => {
    const r = data?.resources.find(x => x.id === id);
    if (r && !window.confirm(`Delete "${r.name || 'this resource'}"?`)) return;
    mutate(d => ({ ...d, resources: d.resources.filter(x => x.id !== id) }));
    setSelRes(s => (s === id ? null : s));
  }, [data, mutate]);

  const doSave = useCallback(async () => {
    if (!data) return;
    setSaving(true);
    try {
      const res = await saveWelcomeDay(data);
      if (res.ok) { setDirty(false); setStatus('Saved ✓'); }
      else setStatus(res.message || 'Save failed.');
    } catch (e) { setStatus('Save failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  }, [data]);

  // ── Map bootstrap ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: CENTER, zoom: 16, mapTypeId: 'roadmap',
      tilt: 0, heading: 0, rotateControl: false,
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      clickableIcons: false, gestureHandling: 'greedy',
    });
    map.addListener('click', (e) => {
      if (!placingRef.current || !selEventRef.current) return;
      updateEvent(selEventRef.current, { lat: e.latLng.lat(), lng: e.latLng.lng() });
      setPlacing(false); setStatus('Location set.');
    });
    mapInstanceRef.current = map;
    setMapReady(true);
  }, [isLoaded, updateEvent]);

  // ── Draggable event pins ───────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const objs = markerObjsRef.current;
    const alive = new Set();
    events.forEach((ev, i) => {
      if (ev.lat == null || ev.lng == null) return;
      alive.add(ev.id);
      let m = objs.get(ev.id);
      if (!m) {
        m = new window.google.maps.Marker({ map, draggable: true });
        m.addListener('click', () => { setSelEvent(ev.id); setView('events'); });
        m.addListener('dragend', (e) => updateEvent(ev.id, { lat: e.latLng.lat(), lng: e.latLng.lng() }));
        objs.set(ev.id, m);
      }
      const sel = ev.id === selEvent;
      m.setPosition({ lat: ev.lat, lng: ev.lng });
      m.setZIndex(sel ? 500 : 100 + i);
      m.setLabel({ text: ev.icon || '📍', fontSize: sel ? '22px' : '17px' });
      m.setIcon({ path: window.google.maps.SymbolPath.CIRCLE, scale: sel ? 17 : 13,
        fillColor: sel ? '#8e24aa' : '#5c6bc0', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2.5 });
    });
    for (const [id, m] of objs) if (!alive.has(id)) { m.setMap(null); objs.delete(id); }
  }, [events, selEvent, mapReady, updateEvent]);

  // ── Checkpoint node picker: clickable nav nodes when arming "add checkpoint" ─
  const nodePickRef = useRef([]);
  useEffect(() => {
    if (!mapReady || !graphReady) return;
    const map = mapInstanceRef.current;
    const clear = () => { nodePickRef.current.forEach(m => m.setMap(null)); nodePickRef.current = []; };
    clear();
    if (!picking || !selEvent) return;
    const render = () => {
      clear();
      const b = map.getBounds(); if (!b) return;
      if ((map.getZoom() || 0) < 17) { setStatus('Zoom in to see nodes, then click one to add a checkpoint.'); return; }
      let n = 0;
      for (const node of mapDataRef.current.nodes.values()) {
        if (node.lat == null || !b.contains({ lat: node.lat, lng: node.lng })) continue;
        if (n++ > 500) break;
        const m = new window.google.maps.Marker({
          map, position: { lat: node.lat, lng: node.lng }, zIndex: 900, title: nodeLabel(node),
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 5.5, fillColor: '#00897b', fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 1.5 },
        });
        m.addListener('click', () => addCheckpointRef.current(node));
        nodePickRef.current.push(m);
      }
    };
    const l = map.addListener('idle', render);
    render();
    return () => { window.google.maps.event.removeListener(l); clear(); };
  }, [mapReady, graphReady, picking, selEvent]);

  // ── Selected event's destination + numbered checkpoints + order preview ────
  const overlayRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return;
    overlayRef.current.forEach(o => o.setMap(null));
    overlayRef.current = [];
    const map = mapInstanceRef.current, md = mapDataRef.current;
    const ev = events.find(e => e.id === selEvent);
    if (!ev || !md) return;
    const cpNodes = (ev.checkpoints || []).map(c => md.nodes.get(c.nodeId)).filter(Boolean);
    cpNodes.forEach((node, i) => overlayRef.current.push(new window.google.maps.Marker({
      map, position: { lat: node.lat, lng: node.lng }, zIndex: 950,
      label: { text: String(i + 1), color: '#fff', fontSize: '11px', fontWeight: '700' },
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#ff7043', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
    })));
    const dest = ev.destNodeId ? md.nodes.get(ev.destNodeId) : null;
    if (dest) overlayRef.current.push(new window.google.maps.Marker({
      map, position: { lat: dest.lat, lng: dest.lng }, zIndex: 960,
      label: { text: '★', color: '#fff', fontSize: '13px' },
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: '#8e24aa', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
    }));
    const path = [...cpNodes.map(n => ({ lat: n.lat, lng: n.lng })), ...(dest ? [{ lat: dest.lat, lng: dest.lng }] : [])];
    if (path.length >= 2) overlayRef.current.push(new window.google.maps.Polyline({
      map, path, strokeColor: '#ff7043', strokeOpacity: 0.9, strokeWeight: 3, zIndex: 940,
    }));
    return () => { overlayRef.current.forEach(o => o.setMap(null)); overlayRef.current = []; };
  }, [mapReady, events, selEvent]);

  // Disarm the pickers when leaving the event.
  useEffect(() => { setPicking(false); setPlacing(false); }, [selEvent]);

  const selectedEvent = useMemo(() => events.find(e => e.id === selEvent) || null, [events, selEvent]);
  const selectedRes = useMemo(() => (data?.resources || []).find(r => r.id === selRes) || null, [data, selRes]);

  if (!apiKey) return <div className="map-no-key"><h3>Google Maps API Key Required</h3><p>Add <code>REACT_APP_MAPS_KEY</code> to your <code>.env.local</code>.</p></div>;
  if (loadError) return <div className="map-no-key"><h3>Failed to load Google Maps</h3></div>;

  return (
    <div className="wd-page wd-editor">
      <aside className="wd-itinerary wd-edit-side">
        <div className="wd-edit-head">
          <strong>🎒 Welcome Day Editor</strong>
          <button className="places-save" onClick={doSave} disabled={saving || !dirty}>{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>
        </div>
        {status && <div className="places-status">{status}</div>}

        <div className="wd-titlefields">
          <input className="pf-input" placeholder="Event title (e.g. Welcome Day)" value={data?.title || ''} onChange={e => mutate(d => ({ ...d, title: e.target.value }))} />
          <input className="pf-input" placeholder="Subtitle" value={data?.subtitle || ''} onChange={e => mutate(d => ({ ...d, subtitle: e.target.value }))} />
          <label className="wd-edit-toggle">
            <input type="checkbox" checked={!!data?.avoidTunnels} onChange={e => mutate(d => ({ ...d, avoidTunnels: e.target.checked }))} />
            <span>🚇 Avoid tunnels when routing attendees</span>
          </label>
        </div>

        <div className="places-seg">
          <button className={view === 'events' ? 'on' : ''} onClick={() => setView('events')}>Itinerary</button>
          <button className={view === 'resources' ? 'on' : ''} onClick={() => setView('resources')}>Resources</button>
        </div>

        {view === 'events' ? (
          selectedEvent ? (
            <EventForm ev={selectedEvent} placing={placing} picking={picking}
              rooms={roomIndex}
              onBack={() => setSelEvent(null)}
              onChange={(patch) => updateEvent(selectedEvent.id, patch)}
              onPlace={() => { setPicking(false); setPlacing(true); setStatus('Click the map to set this event.'); }}
              onSetDestination={setDestination}
              onClearDestination={clearDestination}
              onTogglePick={() => { setPlacing(false); setPicking(p => !p); }}
              onRemoveCheckpoint={removeCheckpoint}
              onMoveCheckpoint={moveCheckpoint}
              onDelete={() => deleteEvent(selectedEvent.id)} />
          ) : (
            <>
              <button className="wd-add" onClick={addEvent}>＋ Add event</button>
              <div className="wd-timeline wd-edit-list">
                {events.length === 0 && <div className="wd-empty">No events yet.</div>}
                {events.map(ev => (
                  <div key={ev.id} className="wd-evt-row">
                    <button className="wd-evt" onClick={() => setSelEvent(ev.id)}>
                      <span className="wd-evt-time">{prettyTime(ev.time)}</span>
                      <span className="wd-evt-dot">{ev.icon || '📍'}</span>
                      <span className="wd-evt-text">
                        <span className="wd-evt-title">{ev.title || 'Untitled event'}</span>
                        {ev.lat == null && <span className="wd-evt-loc wd-warn">No location</span>}
                      </span>
                    </button>
                    <button className="pb-del" onClick={() => deleteEvent(ev.id)} aria-label="Delete">✕</button>
                  </div>
                ))}
              </div>
            </>
          )
        ) : (
          selectedRes ? (
            <ResourceForm res={selectedRes} rooms={roomIndex} onBack={() => setSelRes(null)}
              onChange={(patch) => updateRes(selectedRes.id, patch)}
              onSetLocation={setResLocation} onClearLocation={clearResLocation}
              onDelete={() => deleteRes(selectedRes.id)} />
          ) : (
            <>
              <button className="wd-add" onClick={addRes}>＋ Add resource</button>
              <div className="wd-timeline wd-edit-list">
                {(data?.resources || []).length === 0 && <div className="wd-empty">No resources yet.</div>}
                {(data?.resources || []).map(r => (
                  <div key={r.id} className="wd-evt-row">
                    <button className="wd-evt" onClick={() => setSelRes(r.id)}>
                      <span className="wd-evt-dot">{r.icon || '🎒'}</span>
                      <span className="wd-evt-text">
                        <span className="wd-evt-title">{r.name || 'Untitled'}</span>
                        {r.synopsis && <span className="wd-evt-loc">{r.synopsis}</span>}
                        {r.location && <span className="wd-evt-loc wd-res-loc">📍 {r.location}</span>}
                      </span>
                    </button>
                    {r.location && (
                      <button className="pb-del wd-res-locdel" onClick={() => updateRes(r.id, { destNodeId: null, location: '' })}
                        title="Remove pickup location" aria-label="Remove pickup location">📍✕</button>
                    )}
                    <button className="pb-del" onClick={() => deleteRes(r.id)} aria-label="Delete resource">✕</button>
                  </div>
                ))}
              </div>
              <div className="wd-preview">
                <WelcomeBackpack resources={data?.resources || []} label="🎒 Arrange backpack"
                  editable selectedId={arrSel} onSelect={setArrSel}
                  onPlace={(id, place) => updateRes(id, { place })} />
                <div className="wd-preview-hint">Open the backpack to drag, scale &amp; rotate items into place.</div>
              </div>
            </>
          )
        )}
      </aside>

      <div className="wd-map-wrap">
        <div className="wd-map" ref={mapRef} />
        {placing && <div className="wd-placing">Click the map to place the event pin</div>}
        {picking && <div className="wd-placing wd-picking">Zoom in and click teal nodes to add checkpoints (in order)</div>}
      </div>
    </div>
  );
}

// ── Field helpers (self-contained; no cross-file imports) ────────────────────
function Row({ label, children }) {
  return <label className="pf-field"><span className="pf-label">{label}</span>{children}</label>;
}
function Text({ label, value, onChange, placeholder, area }) {
  return (
    <Row label={label}>
      {area
        ? <textarea className="pf-input" rows={3} value={value || ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
        : <input className="pf-input" value={value || ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} />}
    </Row>
  );
}

// A room search box (building + room #) → picks a destination room.
function RoomSearch({ rooms, onPick, placeholder }) {
  const [q, setQ] = useState('');
  const results = useMemo(() => (q.trim() ? searchRoomList(rooms, q) : []), [rooms, q]);
  return (
    <div className="wd-roomsearch">
      <input className="pf-input" placeholder={placeholder} value={q} onChange={e => setQ(e.target.value)} />
      {results.length > 0 && (
        <div className="np-results">
          {results.map(r => (
            <button key={`${r.building}-${r.roomNumber}`} className="np-result" onClick={() => { onPick(r); setQ(''); }}>
              <span className="np-result-room">{r.roomNumber}</span>
              <span className="np-result-bldg">{r.buildingName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EventForm({ ev, placing, picking, rooms, onBack, onChange, onPlace, onSetDestination, onClearDestination, onTogglePick, onRemoveCheckpoint, onMoveCheckpoint, onDelete }) {
  const cps = ev.checkpoints || [];
  return (
    <div className="places-form">
      <div className="pf-head">
        <span className="pf-head-icon">{ev.icon || '📍'}</span>
        <span className="pf-head-title">{ev.title || 'Untitled event'}</span>
        <button className="pf-del" onClick={onDelete}>Delete</button>
      </div>
      <button className="places-back" onClick={onBack}>← All events</button>

      {/* Basics */}
      <div className="pf-row2">
        <Text label="Time" value={ev.time} onChange={v => onChange({ time: v })} placeholder="09:30" />
        <Text label="Icon" value={ev.icon} onChange={v => onChange({ icon: v })} placeholder="📍" />
      </div>
      <Text label="Title" value={ev.title} onChange={v => onChange({ title: v })} placeholder="Welcome Address" />
      <Text label="Description" value={ev.description} onChange={v => onChange({ description: v })} placeholder="What happens at this event…" area />
      <ImagePicker label="Photo" mode="single" value={ev.image} onChange={v => onChange({ image: v })} uploadFolder="welcome_day/events" />

      {/* Wayfinding */}
      <div className="wd-section">Where students go</div>
      <Row label="Destination room">
        {ev.destNodeId ? (
          <div className="wd-dest-set">
            <span className="wd-dest-name">🎯 {ev.location || 'Room set'}</span>
            <button className="pf-x" onClick={onClearDestination} aria-label="Clear destination">✕</button>
          </div>
        ) : (
          <RoomSearch rooms={rooms} onPick={onSetDestination} placeholder="Search a room — e.g. Armes 209" />
        )}
      </Row>

      <Row label={`Checkpoints (${cps.length}) — doors to go through, in order`}>
        <div className="wd-cps">
          {cps.map((c, i) => (
            <div className="wd-cp-row" key={i}>
              <span className="wd-cp-n">{i + 1}</span>
              <span className="wd-cp-lbl">{c.label || c.nodeId}</span>
              <span className="wd-cp-actions">
                <button className="wd-cp-btn" disabled={i === 0} onClick={() => onMoveCheckpoint(i, -1)} aria-label="Up">▲</button>
                <button className="wd-cp-btn" disabled={i === cps.length - 1} onClick={() => onMoveCheckpoint(i, 1)} aria-label="Down">▼</button>
                <button className="wd-cp-btn del" onClick={() => onRemoveCheckpoint(i)} aria-label="Remove">✕</button>
              </span>
            </div>
          ))}
          <button className={`wd-add ${picking ? 'on' : ''}`} onClick={onTogglePick}>
            {picking ? '✓ Click nodes on the map…' : '＋ Add checkpoint (pick on map)'}
          </button>
        </div>
      </Row>

      {/* Fallback pin for outdoor events with no room */}
      <details className="pf-advanced">
        <summary>Manual pin / location</summary>
        <Text label="Location label" value={ev.location} onChange={v => onChange({ location: v })} placeholder="Meet at the Quad" />
        <Row label="Map pin">
          <div className="pf-loc">
            <span className="pf-loc-text">{ev.lat != null ? `${ev.lat.toFixed(5)}, ${ev.lng.toFixed(5)}` : 'No location yet'}</span>
            <button className={`pf-btn ${placing ? 'on' : ''}`} onClick={onPlace}>{placing ? 'Click map…' : '📌 Set on map'}</button>
          </div>
        </Row>
      </details>
    </div>
  );
}

function ResourceForm({ res, rooms = [], onBack, onChange, onSetLocation, onClearLocation, onDelete }) {
  return (
    <div className="places-form">
      <div className="pf-head">
        <span className="pf-head-icon">{res.icon || '🎒'}</span>
        <span className="pf-head-title">{res.name || 'Untitled'}</span>
        <button className="pf-del" onClick={onDelete}>Delete</button>
      </div>
      <button className="places-back" onClick={onBack}>← All resources</button>
      <div className="pf-row2">
        <Text label="Name" value={res.name} onChange={v => onChange({ name: v })} placeholder="Student ID Card" />
        <Text label="Icon (emoji)" value={res.icon} onChange={v => onChange({ icon: v })} placeholder="🪪" />
      </div>
      <Text label="Synopsis (hover text)" value={res.synopsis} onChange={v => onChange({ synopsis: v })} placeholder="Your all-access campus card." />
      <RichBodyEditor value={res.details} onChange={v => onChange({ details: v })} uploadFolder="welcome_day/resources" />
      <ImagePicker label="Backpack image (the item in the bag)" mode="single" value={res.image} onChange={v => onChange({ image: v })} uploadFolder="welcome_day/resources" />

      {/* Where students pick it up — a room students can wayfind to. */}
      <div className="wd-section">Where students get it</div>
      <Row label="Pickup location">
        {res.destNodeId ? (
          <div className="wd-dest-set">
            <span className="wd-dest-name">📍 {res.location || 'Location set'}</span>
            <button className="pf-x" onClick={onClearLocation} aria-label="Clear location">✕</button>
          </div>
        ) : (
          <RoomSearch rooms={rooms} onPick={onSetLocation} placeholder="Search a room — e.g. University Centre 200" />
        )}
      </Row>
    </div>
  );
}
